const { useCallback } = require('react')
const agridentialService = require('./services/agridential.hook.service')
const haugiangService = require('./services/haugiang.hook.service')
const mbcService = require('./services/mbc.hook.service')
const vibiService = require('./services/vibi.hook.service')
const vibi2Service = require('./services/vibi2.hook.service')

const moment = require('moment')

const createBlockchainHandler = (networkHandlers, currentNetwork, dataTransformer = data => data) => {
    return async (...args) => {
        try {
            const handlers = {
                agd: networkHandlers.agd,
                hg: networkHandlers.hg,
                mbc: networkHandlers.mbc,
                vibi: networkHandlers.vibi,
                vibi2: networkHandlers.vibi2
            }
            const handler = handlers[currentNetwork]
            if (!handler) return null

            const response = await handler(...args)
            return dataTransformer(response)
        } catch (error) {
            return null
        }
    }
}

const getTimestampRanges = days => {
    const ranges = []
    for (let i = days - 1; i >= 0; i--) {
        const startTime = moment().subtract(i, 'days').startOf('day')
        const endTime = moment().subtract(i, 'days').endOf('day')
        ranges.push([startTime.valueOf(), endTime.valueOf()])
    }
    return ranges
}

const useBlockchainData = network => {
    const networkServices = {
        agd: agridentialService(),
        hg: haugiangService(),
        mbc: mbcService(),
        vibi: vibiService(),
        vibi2: vibi2Service()
    }

    const transformBlockData = useCallback(block => {
        if (!block) return null

        return {
            number: Number(block.number),
            hash: block.hash,
            mixHash: block.mixHash,
            parentHash: block.parentHash,
            nonce: Number(block.nonce),

            sha3Uncles: block.sha3Uncles,
            logsBloom: block.logsBloom,
            transactionsRoot: block.transactionsRoot,
            stateRoot: block.stateRoot,
            receiptsRoot: block.receiptsRoot,
            validator: block.miner || block.validator,
            difficulty: block.difficulty,
            totalDifficulty: block.totalDifficulty,
            extraData: block.extraData,
            size: Number(block.size),
            gasLimit: Number(block.gasLimit),
            gasUsed: Number(block.gasUsed),
            totalTransactionFees: Number(block.totalTransactionFees),
            timestamp: Number(block.timestamp),

            transactionCount: Number(block.transactionCount),
            internalTransactionCount: Number(block.internalTransactionCount),
            blobTransactionCount: Number(block.blobTransactionCount),
            blobCount: Number(block.blobCount),
            baseFeePerGas: Number(block.baseFeePerGas),
            withdrawalsRoot: block.withdrawalsRoot,
            withdrawals: Number(block.withdrawals),
            blobGasUsed: Number(block.blobGasUsed),
            blobGasPrice: Number(block.blobGasPrice),
            blobGasLimit: Number(block.blobGasLimit),
            excessBlobGas: Number(block.excessBlobGas),
            parentBeaconBlockRoot: block.parentBeaconBlockRoot,

            beacon: {
                slot: Number(block.beacon.slot),
                epoch: Number(block.beacon.epoch),
                blockRoot: block.beacon.blockRoot,
                proposerIndex: Number(block.beacon.proposerIndex),
                blockRandomness: block.beacon.blockRandomness,
                randaoReveal: block.beacon.randaoReveal,
                depositRoot: block.beacon.depositRoot,
                depositCount: Number(block.beacon.depositCount),
                graffiti: block.beacon.graffiti,
                proposerSlashings: block.beacon.proposerSlashings,
                attesterSlashings: block.beacon.attesterSlashings,
                attestations: block.beacon.attestations,
                deposits: block.beacon.deposits,
                voluntaryExits: block.beacon.voluntaryExits
            }
        }
    }, [])

    const transformTransactionData = useCallback(transaction => {
        if (!transaction) return null

        return {
            abi: transaction.abi,
            accessList: transaction.accessList,
            blobCommitments: transaction.blobCommitments,
            blobGasPrice: Number(transaction.blobGasPrice),
            blobGasUsed: Number(transaction.blobGasUsed),
            blobVersionedHashes: transaction.blobVersionedHashes,
            blockHash: transaction.blockHash,
            blockNumber: Number(transaction.blockNumber),
            blockTimestamp: Number(transaction.blockTimestamp),
            chainId: Number(transaction.chainId),
            contractAddress: transaction.contractAddress,
            cumulativeGasUsed: Number(transaction.cumulativeGasUsed),
            effectiveGasPrice: Number(transaction.effectiveGasPrice),
            from: transaction.from,
            functionName: transaction.functionName,
            gas: Number(transaction.gas),
            gasPrice: Number(transaction.gasPrice),
            gasUsed: Number(transaction.gasUsed),
            hash: transaction.hash,
            input: transaction.input,
            logs: transaction.logs,
            maxFeePerBlobGas: Number(transaction.maxFeePerBlobGas),
            maxFeePerGas: Number(transaction.maxFeePerGas),
            maxPriorityFeePerGas: Number(transaction.maxPriorityFeePerGas),
            nonce: Number(transaction.nonce),
            status: transaction.status,
            to: transaction.to,
            transactionIndex: Number(transaction.transactionIndex),
            type: Number(transaction.type),
            value: Number(transaction.value)
        }
    }, [])

    const transformInternalTransactionData = useCallback(block => {
        if (!block) return null

        return {
            blockHash: block.blockHash,
            blockNumber: Number(block.blockNumber),
            blockTimestamp: Number(block.blockTimestamp),
            transactions: block.transactions
        }
    }, [])

    const transformAddressData = useCallback(address => {
        if (!address) return null

        return {
            balance: Number(address.balance),
            type: address.type,
            information: address.information,
            internalTransactions: address.internalTransactions,
            tokens: address.tokens,
            tokenTransfers: address.tokenTransfers,
            nftTransfers: address.nftTransfers,
            transactions: address.transactions
        }
    }, [])

    const transformTokenData = useCallback(token => {
        if (!token) return null

        return {
            tokenAddress: token.tokenAddress,
            tokenName: token.tokenName,
            tokenSymbol: token.tokenSymbol,
            tokenDecimals: Number(token.tokenDecimals),
            totalSupply: Number(token.totalSupply),
            tokenType: token.tokenType,
            totalHolders: Number(token.totalHolders),
            totalTransfers: Number(token.totalTransfers)
        }
    }, [])

    const transformEpochData = useCallback(epoch => {
        if (!epoch) return null

        return {
            epoch: Number(epoch.epoch),
            slotsCount: Number(epoch.slotsCount),
            timestamp: Number(epoch.timestamp),
            attestationsCount: Number(epoch.attestationsCount),
            depositsCount: Number(epoch.depositsCount),
            proposerSlashingsCount: Number(epoch.proposerSlashingsCount),
            attesterSlashingsCount: Number(epoch.attesterSlashingsCount),
            voluntaryExitsCount: Number(epoch.voluntaryExitsCount),
            skippedSlotsCount: Number(epoch.skippedSlotsCount),
            forkedSlotsCount: Number(epoch.forkedSlotsCount),
            eligibleBalance: Number(epoch.eligibleBalance),
            votedBalance: Number(epoch.votedBalance),
            isFinalized: epoch.isFinalized
        }
    }, [])

    const transformSlotData = useCallback(slot => {
        if (!slot) return null

        return {
            epoch: Number(slot.epoch),
            slot: Number(slot.slot),
            timestamp: Number(slot.timestamp),
            proposerIndex: Number(slot.proposerIndex),
            proposerPubkey: slot.proposerPubkey,
            blockRoot: slot.blockRoot,
            parentRoot: slot.parentRoot,
            stateRoot: slot.stateRoot,
            randaoReveal: slot.randaoReveal,
            graffiti: slot.graffiti,
            eth1BlockHash: slot.eth1BlockHash,
            eth1DepositRoot: slot.eth1DepositRoot,
            eth1DepositCount: Number(slot.eth1DepositCount),
            signature: slot.signature,
            attestationsCount: Number(slot.attestationsCount),
            depositsCount: Number(slot.depositsCount),
            proposerSlashingsCount: Number(slot.proposerSlashingsCount),
            attesterSlashingsCount: Number(slot.attesterSlashingsCount),
            voluntaryExitsCount: Number(slot.voluntaryExitsCount)
        }
    }, [])

    const getTransactionPerDay = useCallback(
        createBlockchainHandler(
            {
                agd: (days = 7) => {
                    const timestamps = getTimestampRanges(days)
                    return networkServices.agd.getTransactionByDeltaTime(timestamps)
                },
                hg: (days = 7) => {
                    const timestamps = getTimestampRanges(days)
                    return networkServices.hg.getTransactionByDeltaTime(timestamps)
                },
                mbc: (days = 7) => {
                    const timestamps = getTimestampRanges(days)
                    return networkServices.mbc.getTransactionByDeltaTime(timestamps)
                },
                vibi: (days = 7) => {
                    const timestamps = getTimestampRanges(days)
                    return networkServices.vibi.getTransactionByDeltaTime(timestamps)
                },
                vibi2: (days = 7) => {
                    const timestamps = getTimestampRanges(days)
                    return networkServices.vibi2.getTransactionByDeltaTime(timestamps)
                }
            },
            network,
            res => res || []
        ),
        [network]
    )

    const getGeneralInfo = useCallback(
        createBlockchainHandler(
            {
                agd: networkServices.agd.getGeneralInfo,
                hg: networkServices.hg.getGeneralInfo,
                mbc: networkServices.mbc.getGeneralInfo,
                vibi: networkServices.vibi.getGeneralInfo,
                vibi2: networkServices.vibi2.getGeneralInfo
            },
            network,
            res => ({ ...res })
        ),
        [network]
    )

    const getBlocks = useCallback(
        createBlockchainHandler(
            {
                agd: (page = 1, limit = 25) => {
                    return networkServices.agd.getBlocks(page, limit)
                },
                hg: (page = 1, limit = 25) => {
                    return networkServices.hg.getBlocks(page, limit)
                },
                mbc: (page = 1, limit = 25) => {
                    return networkServices.mbc.getBlocks(page, limit)
                },
                vibi: (page = 1, limit = 25) => {
                    return networkServices.vibi.getBlocks(page, limit)
                },
                vibi2: (page = 1, limit = 25) => {
                    return networkServices.vibi2.getBlocks(page, limit)
                }
            },
            network,
            res => ({
                ...res,
                items: res.items.map(transformBlockData)
            })
        ),
        [network]
    )

    const getBlock = useCallback(
        createBlockchainHandler(
            {
                agd: blockNumber => networkServices.agd.getBlock(blockNumber),
                hg: blockNumber => networkServices.hg.getBlock(blockNumber),
                mbc: blockNumber => networkServices.mbc.getBlock(blockNumber),
                vibi: blockNumber => networkServices.vibi.getBlock(blockNumber),
                vibi2: blockNumber => networkServices.vibi2.getBlock(blockNumber)
            },
            network,
            transformBlockData
        ),
        [network, transformBlockData]
    )

    const getTransactionsStatistics = useCallback(
        createBlockchainHandler(
            {
                agd: () => {
                    return networkServices.agd.getTransactionsStatistics()
                },
                hg: () => {
                    return networkServices.hg.getTransactionsStatistics()
                },
                mbc: () => {
                    return networkServices.mbc.getTransactionsStatistics()
                },
                vibi: () => {
                    return networkServices.vibi.getTransactionsStatistics()
                },
                vibi2: () => {
                    return networkServices.vibi2.getTransactionsStatistics()
                }
            },
            network,
            res => ({ ...res })
        ),
        [network]
    )

    const getTransactions = useCallback(
        createBlockchainHandler(
            {
                agd: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.agd.getTransactions(page, limit, filter)
                },
                hg: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.hg.getTransactions(page, limit, filter)
                },
                mbc: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.mbc.getTransactions(page, limit, filter)
                },
                vibi: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.vibi.getTransactions(page, limit, filter)
                },
                vibi2: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.vibi2.getTransactions(page, limit, filter)
                }
            },
            network,
            res => ({
                ...res,
                items: res.items.map(transformTransactionData)
            })
        ),
        [network, transformTransactionData]
    )

    const getPendingTransactions = useCallback(
        createBlockchainHandler(
            {
                agd: (page = 1, limit = 25) => {
                    return networkServices.agd.getPendingTransactions(page, limit)
                },
                hg: (page = 1, limit = 25) => {
                    return networkServices.hg.getPendingTransactions(page, limit)
                },
                mbc: (page = 1, limit = 25) => {
                    return networkServices.mbc.getPendingTransactions(page, limit)
                },
                vibi: (page = 1, limit = 25) => {
                    return networkServices.vibi.getPendingTransactions(page, limit)
                },
                vibi2: (page = 1, limit = 25) => {
                    return networkServices.vibi2.getPendingTransactions(page, limit)
                }
            },
            network,
            res => ({
                ...res,
                items: res.items.map(transformTransactionData)
            })
        ),
        [network, transformTransactionData]
    )

    const getTransaction = useCallback(
        createBlockchainHandler(
            {
                agd: transactionHash => {
                    return networkServices.agd.getTransaction(transactionHash)
                },
                hg: transactionHash => {
                    return networkServices.hg.getTransaction(transactionHash)
                },
                mbc: transactionHash => {
                    return networkServices.mbc.getTransaction(transactionHash)
                },
                vibi: transactionHash => {
                    return networkServices.vibi.getTransaction(transactionHash)
                },
                vibi2: transactionHash => {
                    return networkServices.vibi2.getTransaction(transactionHash)
                }
            },
            network,
            res => ({
                ...res,
                details: transformTransactionData(res.details)
            })
        ),
        [network, transformTransactionData]
    )

    const getInternalTransactions = useCallback(
        createBlockchainHandler(
            {
                agd: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.agd.getInternalTransactions(page, limit, filter)
                },
                hg: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.hg.getInternalTransactions(page, limit, filter)
                },
                mbc: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.mbc.getInternalTransactions(page, limit, filter)
                },
                vibi: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.vibi.getInternalTransactions(page, limit, filter)
                },
                vibi2: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.vibi2.getInternalTransactions(page, limit, filter)
                }
            },
            network,
            res => ({
                ...res,
                items: res.items.map(transformInternalTransactionData)
            })
        ),
        [network, transformInternalTransactionData]
    )

    const getVerifiedContracts = useCallback(
        createBlockchainHandler(
            {
                agd: (page = 1, limit = 25) => {
                    return networkServices.agd.getVerifiedContracts(page, limit)
                },
                hg: (page = 1, limit = 25) => {
                    return networkServices.hg.getVerifiedContracts(page, limit)
                },
                mbc: (page = 1, limit = 25) => {
                    return networkServices.mbc.getVerifiedContracts(page, limit)
                },
                vibi: (page = 1, limit = 25) => {
                    return networkServices.vibi.getVerifiedContracts(page, limit)
                },
                vibi2: (page = 1, limit = 25) => {
                    return networkServices.vibi2.getVerifiedContracts(page, limit)
                }
            },
            network,
            res => ({ ...res })
        ),
        [network]
    )

    const getSimilarContracts = useCallback(
        createBlockchainHandler(
            {
                agd: (page = 1, limit = 25, address) => {
                    return networkServices.agd.getSimilarContracts(page, limit, address)
                },
                hg: (page = 1, limit = 25, address) => {
                    return networkServices.hg.getSimilarContracts(page, limit, address)
                },
                mbc: (page = 1, limit = 25, address) => {
                    return networkServices.mbc.getSimilarContracts(page, limit, address)
                },
                vibi: (page = 1, limit = 25, address) => {
                    return networkServices.vibi.getSimilarContracts(page, limit, address)
                },
                vibi2: (page = 1, limit = 25, address) => {
                    return networkServices.vibi2.getSimilarContracts(page, limit, address)
                }
            },
            network,
            res => ({ ...res })
        ),
        [network]
    )

    const getContractsStatistics = useCallback(
        createBlockchainHandler(
            {
                agd: () => {
                    return networkServices.agd.getContractsStatistics()
                },
                hg: () => {
                    return networkServices.hg.getContractsStatistics()
                },
                mbc: () => {
                    return networkServices.mbc.getContractsStatistics()
                },
                vibi: () => {
                    return networkServices.vibi.getContractsStatistics()
                },
                vibi2: () => {
                    return networkServices.vibi2.getContractsStatistics()
                }
            },
            network,
            res => ({ ...res })
        ),
        [network]
    )

    const getAddressOverview = useCallback(
        createBlockchainHandler(
            {
                agd: address => {
                    return networkServices.agd.getAddressOverview(address)
                },
                hg: address => {
                    return networkServices.hg.getAddressOverview(address)
                },
                mbc: address => {
                    return networkServices.mbc.getAddressOverview(address)
                },
                vibi: address => {
                    return networkServices.vibi.getAddressOverview(address)
                },
                vibi2: address => {
                    return networkServices.vibi2.getAddressOverview(address)
                }
            },
            network,
            res => ({ ...res })
        ),
        [network]
    )

    const getAddress = useCallback(
        createBlockchainHandler(
            {
                agd: address => {
                    return networkServices.agd.getAddress(address)
                },
                hg: address => {
                    return networkServices.hg.getAddress(address)
                },
                mbc: address => {
                    return networkServices.mbc.getAddress(address)
                },
                vibi: address => {
                    return networkServices.vibi.getAddress(address)
                },
                vibi2: address => {
                    return networkServices.vibi2.getAddress(address)
                }
            },
            network,
            transformAddressData
        ),
        [network, transformAddressData]
    )

    const getTokens = useCallback(
        createBlockchainHandler(
            {
                agd: (page = 1, limit = 25) => {
                    return networkServices.agd.getTokens(page, limit)
                },
                hg: (page = 1, limit = 25) => {
                    return networkServices.hg.getTokens(page, limit)
                },
                mbc: (page = 1, limit = 25) => {
                    return networkServices.mbc.getTokens(page, limit)
                },
                vibi: (page = 1, limit = 25) => {
                    return networkServices.vibi.getTokens(page, limit)
                },
                vibi2: (page = 1, limit = 25) => {
                    return networkServices.vibi2.getTokens(page, limit)
                }
            },
            network,
            res => ({
                ...res,
                items: res.items.map(transformTokenData)
            })
        ),
        [network, transformTokenData]
    )

    const getTokenOverview = useCallback(
        createBlockchainHandler(
            {
                agd: address => {
                    return networkServices.agd.getTokenOverview(address)
                },
                hg: address => {
                    return networkServices.hg.getTokenOverview(address)
                },
                mbc: address => {
                    return networkServices.mbc.getTokenOverview(address)
                },
                vibi: address => {
                    return networkServices.vibi.getTokenOverview(address)
                },
                vibi2: address => {
                    return networkServices.vibi2.getTokenOverview(address)
                }
            },
            network,
            res => ({ ...res })
        ),
        [network]
    )

    const getTokenTransfers = useCallback(
        createBlockchainHandler(
            {
                agd: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.agd.getTokenTransfers(page, limit, filter)
                },
                hg: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.hg.getTokenTransfers(page, limit, filter)
                },
                mbc: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.mbc.getTokenTransfers(page, limit, filter)
                },
                vibi: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.vibi.getTokenTransfers(page, limit, filter)
                },
                vibi2: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.vibi2.getTokenTransfers(page, limit, filter)
                }
            },
            network,
            res => ({ ...res })
        ),
        [network]
    )

    const getTokenHolders = useCallback(
        createBlockchainHandler(
            {
                agd: (address, page = 1, limit = 25) => {
                    return networkServices.agd.getTokenHolders(address, page, limit)
                },
                hg: (address, page = 1, limit = 25) => {
                    return networkServices.hg.getTokenHolders(address, page, limit)
                },
                mbc: (address, page = 1, limit = 25) => {
                    return networkServices.mbc.getTokenHolders(address, page, limit)
                },
                vibi: (address, page = 1, limit = 25) => {
                    return networkServices.vibi.getTokenHolders(address, page, limit)
                },
                vibi2: (address, page = 1, limit = 25) => {
                    return networkServices.vibi2.getTokenHolders(address, page, limit)
                }
            },
            network,
            res => ({ ...res })
        ),
        [network]
    )

    const getTokenInventory = useCallback(
        createBlockchainHandler(
            {
                agd: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.agd.getTokenInventory(page, limit, filter)
                },
                hg: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.hg.getTokenInventory(page, limit, filter)
                },
                mbc: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.mbc.getTokenInventory(page, limit, filter)
                },
                vibi: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.vibi.getTokenInventory(page, limit, filter)
                },
                vibi2: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.vibi2.getTokenInventory(page, limit, filter)
                }
            },
            network,
            res => ({ ...res })
        ),
        [network]
    )

    const getNfts = useCallback(
        createBlockchainHandler(
            {
                agd: (page = 1, limit = 25) => {
                    return networkServices.agd.getNfts(page, limit)
                },
                hg: (page = 1, limit = 25) => {
                    return networkServices.hg.getNfts(page, limit)
                },
                mbc: (page = 1, limit = 25) => {
                    return networkServices.mbc.getNfts(page, limit)
                },
                vibi: (page = 1, limit = 25) => {
                    return networkServices.vibi.getNfts(page, limit)
                },
                vibi2: (page = 1, limit = 25) => {
                    return networkServices.vibi2.getNfts(page, limit)
                }
            },
            network,
            res => ({
                ...res,
                items: res.items.map(transformTokenData)
            })
        ),
        [network, transformTokenData]
    )

    const getNftsLatestMints = useCallback(
        createBlockchainHandler(
            {
                agd: (page = 1, limit = 25) => {
                    return networkServices.agd.getNftsLatestMints(page, limit)
                },
                hg: (page = 1, limit = 25) => {
                    return networkServices.hg.getNftsLatestMints(page, limit)
                },
                mbc: (page = 1, limit = 25) => {
                    return networkServices.mbc.getNftsLatestMints(page, limit)
                },
                vibi: (page = 1, limit = 25) => {
                    return networkServices.vibi.getNftsLatestMints(page, limit)
                },
                vibi2: (page = 1, limit = 25) => {
                    return networkServices.vibi2.getNftsLatestMints(page, limit)
                }
            },
            network,
            res => ({
                ...res
            })
        ),
        [network]
    )

    const getNftOverview = useCallback(
        createBlockchainHandler(
            {
                agd: (address, tokenId) => {
                    return networkServices.agd.getNftOverview(address, tokenId)
                },
                hg: (address, tokenId) => {
                    return networkServices.hg.getNftOverview(address, tokenId)
                },
                mbc: (address, tokenId) => {
                    return networkServices.mbc.getNftOverview(address, tokenId)
                },
                vibi: (address, tokenId) => {
                    return networkServices.vibi.getNftOverview(address, tokenId)
                },
                vibi2: (address, tokenId) => {
                    return networkServices.vibi2.getNftOverview(address, tokenId)
                }
            },
            network,
            res => ({
                ...res
            })
        ),
        [network]
    )

    const getBeaconGeneralInfo = useCallback(
        createBlockchainHandler(
            {
                vibi2: networkServices.vibi2.getBeaconGeneralInfo
            },
            network,
            res => ({ ...res })
        ),
        [network]
    )

    const getEpochs = useCallback(
        createBlockchainHandler(
            {
                vibi2: (page = 1, limit = 25) => {
                    return networkServices.vibi2.getEpochs(page, limit)
                }
            },
            network,
            res => ({
                ...res,
                items: res.items.map(transformEpochData)
            })
        ),
        [network]
    )

    const getEpoch = useCallback(
        createBlockchainHandler(
            {
                vibi2: epochNumber => {
                    return networkServices.vibi2.getEpoch(epochNumber)
                }
            },
            network,
            transformEpochData
        ),
        [network, transformEpochData]
    )

    const getSlots = useCallback(
        createBlockchainHandler(
            {
                vibi2: (page = 1, limit = 25, filter = {}) => {
                    return networkServices.vibi2.getSlots(page, limit, filter)
                }
            },
            network,
            res => ({
                ...res,
                items: res.items.map(transformSlotData)
            })
        ),
        [network]
    )

    const getSlot = useCallback(
        createBlockchainHandler(
            {
                vibi2: slotNumber => {
                    return networkServices.vibi2.getSlot(slotNumber)
                }
            },
            network,
            transformSlotData
        ),
        [network, transformSlotData]
    )

    const getValidators = useCallback(
        createBlockchainHandler(
            {
                vibi2: (page = 1, limit = 25, activeTab = 'overview') => {
                    return networkServices.vibi2.getValidators(page, limit, activeTab)
                }
            },
            network,
            res => ({
                ...res
            })
        ),
        [network]
    )

    const verifyContract = useCallback(
        createBlockchainHandler(
            {
                agd: (
                    contractAddress,
                    sourceCode,
                    codeFormat,
                    contractName,
                    compilerVersion,
                    optimization,
                    optimizationRuns,
                    evmVersion,
                    licenseType
                ) => {
                    return networkServices.agd.verifyContract(
                        contractAddress,
                        sourceCode,
                        codeFormat,
                        contractName,
                        compilerVersion,
                        optimization,
                        optimizationRuns,
                        evmVersion,
                        licenseType
                    )
                },
                hg: (
                    contractAddress,
                    sourceCode,
                    codeFormat,
                    contractName,
                    compilerVersion,
                    optimization,
                    optimizationRuns,
                    evmVersion,
                    licenseType
                ) => {
                    return networkServices.hg.verifyContract(
                        contractAddress,
                        sourceCode,
                        codeFormat,
                        contractName,
                        compilerVersion,
                        optimization,
                        optimizationRuns,
                        evmVersion,
                        licenseType
                    )
                },
                mbc: (
                    contractAddress,
                    sourceCode,
                    codeFormat,
                    contractName,
                    compilerVersion,
                    optimization,
                    optimizationRuns,
                    evmVersion,
                    licenseType
                ) => {
                    return networkServices.mbc.verifyContract(
                        contractAddress,
                        sourceCode,
                        codeFormat,
                        contractName,
                        compilerVersion,
                        optimization,
                        optimizationRuns,
                        evmVersion,
                        licenseType
                    )
                },
                vibi: (
                    contractAddress,
                    sourceCode,
                    codeFormat,
                    contractName,
                    compilerVersion,
                    optimization,
                    optimizationRuns,
                    evmVersion,
                    licenseType
                ) => {
                    return networkServices.vibi.verifyContract(
                        contractAddress,
                        sourceCode,
                        codeFormat,
                        contractName,
                        compilerVersion,
                        optimization,
                        optimizationRuns,
                        evmVersion,
                        licenseType
                    )
                },
                vibi2: (
                    contractAddress,
                    sourceCode,
                    codeFormat,
                    contractName,
                    compilerVersion,
                    optimization,
                    optimizationRuns,
                    evmVersion,
                    licenseType
                ) => {
                    return networkServices.vibi2.verifyContract(
                        contractAddress,
                        sourceCode,
                        codeFormat,
                        contractName,
                        compilerVersion,
                        optimization,
                        optimizationRuns,
                        evmVersion,
                        licenseType
                    )
                }
            },
            network,
            res => ({ ...res })
        ),
        [network]
    )

    const getVerificationStatus = useCallback(
        createBlockchainHandler(
            {
                agd: verificationId => {
                    return networkServices.agd.getVerificationStatus(verificationId)
                },
                hg: verificationId => {
                    return networkServices.hg.getVerificationStatus(verificationId)
                },
                mbc: verificationId => {
                    return networkServices.mbc.getVerificationStatus(verificationId)
                },
                vibi: verificationId => {
                    return networkServices.vibi.getVerificationStatus(verificationId)
                },
                vibi2: verificationId => {
                    return networkServices.vibi2.getVerificationStatus(verificationId)
                }
            },
            network,
            res => ({
                ...res
            })
        ),
        [network]
    )

    const getConstructor = useCallback(
        createBlockchainHandler(
            {
                agd: address => {
                    return networkServices.agd.getConstructor(address)
                },
                hg: address => {
                    return networkServices.hg.getConstructor(address)
                },
                mbc: address => {
                    return networkServices.mbc.getConstructor(address)
                },
                vibi: address => {
                    return networkServices.vibi.getConstructor(address)
                },
                vibi2: address => {
                    return networkServices.vibi2.getConstructor(address)
                }
            },
            network,
            res => ({
                ...res
            })
        ),
        [network]
    )

    // Application data
    const getApplications = useCallback(
        createBlockchainHandler(
            {
                agd: address => {
                    return networkServices.agd.getApplications(address)
                },
                hg: address => {
                    return networkServices.hg.getApplications(address)
                },
                mbc: address => {
                    return networkServices.mbc.getApplications(address)
                },
                vibi: address => {
                    return networkServices.vibi.getApplications(address)
                },
                vibi2: address => {
                    return networkServices.vibi2.getApplications(address)
                }
            },
            network,
            res => ({
                ...res
            })
        ),
        [network]
    )

    const getLogsOpinionCollection = useCallback(
        createBlockchainHandler(
            {
                agd: (page, limit, subgraph, tokenId) => {
                    return networkServices.agd.getLogsOpinionCollection(page, limit, subgraph, tokenId)
                },
                hg: (page, limit, subgraph, tokenId) => {
                    return networkServices.hg.getLogsOpinionCollection(page, limit, subgraph, tokenId)
                },
                mbc: (page, limit, subgraph, tokenId) => {
                    return networkServices.mbc.getLogsOpinionCollection(page, limit, subgraph, tokenId)
                },
                vibi: (page, limit, subgraph, tokenId) => {
                    return networkServices.vibi.getLogsOpinionCollection(page, limit, subgraph, tokenId)
                },
                vibi2: (page, limit, subgraph, tokenId) => {
                    return networkServices.vibi2.getLogsOpinionCollection(page, limit, subgraph, tokenId)
                }
            },
            network,
            res => ({
                ...res
            })
        ),
        [network]
    )

    return {
        getTransactionPerDay,
        getGeneralInfo,
        getBlocks,
        getBlock,
        getTransactionsStatistics,
        getTransactions,
        getPendingTransactions,
        getTransaction,
        getInternalTransactions,
        getVerifiedContracts,
        getSimilarContracts,
        getContractsStatistics,
        getAddressOverview,
        getAddress,
        getTokens,
        getTokenOverview,
        getTokenTransfers,
        getTokenHolders,
        getTokenInventory,
        getNfts,
        getNftsLatestMints,
        getNftOverview,
        getBeaconGeneralInfo,
        getEpochs,
        getEpoch,
        getSlots,
        getSlot,
        getValidators,
        verifyContract,
        getVerificationStatus,
        getConstructor,
        getApplications,
        getLogsOpinionCollection
    }
}

module.exports = { useBlockchainData }
